import _ from 'underscore';

import Backbone from 'backbone';
import BaseView from '../base-view';
import i18n from '../utils/i18n';
import Suggest from '../components/suggest/index';
import PriceField from '../components/field-price/index';
import DecimalField from '../components/field-decimal/index';
import FormValidationView from '../components/form-validation/view';
import CurrencyView from '../components/field-currency/view';
import CounterpartyView from '../components/field-counterparty/view';
import DateView from '../components/field-date/view';
import ToggleView from '../components/toggle/view';

export default BaseView.extend({
	events: {
		'change .js-report-type-select': 'checkReportType',
		'input .js-amount': 'updateSettlementAmount',
		'input .js-price': 'updateSettlementAmount',
		'click .js-submit': 'onFormSubmit',
	},

	initialize: function() {
		// reference elements
		this.settlementAmountLabel = this.query(this.el, '.js-settlement-amount-label');
		this.settlementAmountBondsLabel = this.query(this.el, '.js-settlement-amount-bonds-label');

		// create subviews
		this.subviews = {
			reportForm: new FormValidationView({
				el: '.js-form'
			}),

			suggestInstrumentView: new Suggest.Views.Main({
				el: '.js-search-instrument',
				dataUrl: '/autocomplete'
			}),

			counterpartyView: new CounterpartyView({
				el: '.js-counterparty'
			}),

			currencyView: new CurrencyView({
				el: '.js-search-currency'
			}),

			priceView: new PriceField.Views.Main({
				el: '.js-price'
			}),

			chfAmountView: new DecimalField.Views.Main({
				el: '.js-chf-amount'
			}),

			strikePriceView: new DecimalField.Views.Main({
				el: '.js-strike-price'
			}),

			leverageIndicatorView: new DecimalField.Views.Main({
				el: '.js-leverage-indicator'
			}),

			dateExecutionView: new DateView({
				el: '.js-date-execution'
			}),

			dateValueView: new DateView({
				el: '.js-date-value'
			})
		};

		// create auto-next views (inputs)
		this.createLeadingZeroFields();

		// create togglable elements
		this.createToggleElements();

		// check current selected report type
		this.checkReportType();

		// listeners
		this.listenTo(this.subviews.currencyView.model, 'change:Currency', this.onCurrencyChange);
		this.listenTo(this.subviews.dateExecutionView.model, 'change:Date', this.onExecutionDateChange);

		this.onCurrencyChange(null, this.subviews.currencyView.model.get('Currency'));

		// check success notification
		this.removeNotification();
	},

	/**
	 * Add event listener to fields which needs a leading zero
	 **/
	createLeadingZeroFields: function() {
		var fields = this.queryAll('input[data-leading-zero]');

		if (!fields.length) {
			return;
		}

		fields.forEach(function(field, index) {
			field.addEventListener('blur', this.addLeadingZero);
		}, this);
	},

	/**
	 * Add a leading zero to a specific field
	 **/
	addLeadingZero: function(e) {
		var el = e.currentTarget,
			val = el.value;

		if (val.length === 1) {
			el.value = 0 + val;
		}
	},

	/**
	 * create togglable fields (remarks, email)
	 **/
	createToggleElements: function() {
		var toggleTriggers = this.queryAll('.js-toggle-element');

		if (!toggleTriggers.length) {
			return;
		}

		toggleTriggers.forEach(function(triggerEl) {
			return new ToggleView({ el: triggerEl });
		}, this);

	},

	/**
	 * form submit handler
	 * determine the action of the form depending on the clicked submit button
	 * could be report, or add/save to queue
	 **/
	onFormSubmit: function(e) {
		var btn = e.currentTarget,
			formAction = btn.getAttribute('data-action') || '/extranet/tradereporting',
			form = btn.form,
			parsleyForm = this.subviews.reportForm.parsleyForm,
			formIsValid;

		e.preventDefault();

		parsleyForm.validate();
		formIsValid = parsleyForm.validationResult;

		if (formIsValid) {
			// set action of the forms
			form.setAttribute('action', formAction);

			// disable all submit buttons to prevent multiple submissions
			this.queryAll('[type=submit]').forEach(function(submitButton) {
				submitButton.setAttribute('disabled', 'disabled');
			});

			form.submit();
			return false;
		}
	},

	/**
	 * Check the selected report type and toggle other input fields
	 **/
	checkReportType: function() {
		var selectedTradeReportType = this.query('input[name="Type"]:checked'),
			tradeReportType;

		if (selectedTradeReportType) {
			tradeReportType = selectedTradeReportType.value.toLowerCase();

			this.setTradeReportTypeInputs(tradeReportType);
		}
	},

	/**
	 * Set values to specific fields based on the the report type
	 * See Jira issues BX-652, BX-653
	 **/
	setTradeReportTypeInputs: function(reportType) {
		var inputCustomer = this.query('.js-customer');

		if (reportType === 'onesided') {
			inputCustomer.classList.remove('is-hidden');
		} else if (reportType === 'twosided') {
			inputCustomer.classList.add('is-hidden');
			inputCustomer.checked = false;
			inputCustomer.removeAttribute('checked');
		}
	},

	/**
	 * execution date change handler
	 * does an ajax request to get next valid value date
	 * after request, update value date view
	 */
	onExecutionDateChange: function(model, executionDate) {
		// get the date and format it for the backend
		var dateValueView = this.subviews.dateValueView,
			dateExecutionView = this.subviews.dateExecutionView,
			dateFormatted = executionDate.getFullYear() + '-' + (executionDate.getMonth() + 1) + '-' + executionDate.getDate(),
			date;

		dateExecutionView.disable();

		$.ajax({
			url: '/extranet/tradereporting/valuedate',
			data: {executiondate: dateFormatted}
		}).then(function(timestamp) {
			date = new Date(parseInt(timestamp));
			dateValueView.setDate(date);
		}, function() {
			console.warn('error in getting valuedate');
		}).always(function() {
			dateExecutionView.enable();
		});
	},

	/**
	 * currency change handler
	 **/
	onCurrencyChange: function(model, currency) {
		// pass the selected currency to the price field views
		this.subviews.priceView.model.set('currency', currency);

		// update settlement amount
		this.updateSettlementAmount();
	},

	/**
	 * Update the settlement amount in the view
	 **/
	updateSettlementAmount: function() {
		var amount = this.query(this.el, '.js-amount').value || 0,
			price = this.subviews.priceView.model.get('price') || 0,
			currency = this.subviews.priceView.model.get('currency') || 'CHF',
			settlementAmount = parseFloat(amount) * parseFloat(price),
			settlementAmountBonds = settlementAmount / 100;

		this.settlementAmountLabel.textContent = i18n.formatNumberAsCurrency(settlementAmount, currency, 4);
		this.settlementAmountBondsLabel.textContent = i18n.formatNumberAsCurrency(settlementAmountBonds, currency, 4);
	},

	/**
	 * Remove notification if there is one
	 **/
	removeNotification: function() {
		var el = this.query(this.el, '.js-report-notification');

		if (!el) {
			return;
		}

		// remove notification after timeout
		setTimeout(function() {
			el.classList.add('is-hidden');
		}, 3000);
	},

	render: function() {
		// render subviews
		_.each(this.subviews, function(subview) {
			subview.render();
		});

		return this;
	}
});
